import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { green } from '@material-ui/core/colors';
import Fab from '@material-ui/core/Fab';
import { makeStyles, Theme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import SaveIcon from '@material-ui/icons/Save';
import clsx from 'clsx';
import React, { useState } from 'react';

import { Nullable } from 'models/Nullable';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
    wrapper: {
        margin: theme.spacing(0),
        position: 'relative',
        width: '100%',
    },
    buttonSuccess: {
        backgroundColor: green[500],
        '&:hover': {
            backgroundColor: green[700],
        },
    },
    fabProgress: {
        color: green[500],
        position: 'absolute',
        top: -6,
        left: -6,
        zIndex: 1,
    },
    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
}));

export type LoadingPromiseButtonProps = {
    children?: React.ReactNode
    onClick: () => Promise<any>
    fab?: boolean
    ariaLabel: string
    color?: 'primary' | 'secondary'
    size?: 'small' | 'medium' | 'large'
    startIcon?: React.ReactNode
}

export function LoadingButton({ children, onClick, fab, ariaLabel, color = "primary", size = "large", startIcon }: LoadingPromiseButtonProps) {
    const classes = useStyles();
    const [promise, setPromise] = useState<Nullable<Promise<any>>>(null)
    const loading = !!promise
    const [success, setSuccess] = React.useState(false)
    const timer = React.useRef<number>()

    const buttonClassname = clsx({
        [classes.buttonSuccess]: success,
    });

    React.useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    const handleButtonClick = () => {
        if (!loading) {
            setSuccess(false)
            setPromise(onClick().finally(() => setPromise(null)))
            timer.current = window.setTimeout(() => {
                //setSuccess(true)
                setPromise(null)
            }, 5000);
        }
    };

    return (
        <div className={classes.root}>
            {fab ? (
                <div className={classes.wrapper}>
                    <Fab
                        size={size}
                        aria-label={ariaLabel}
                        color={color}
                        className={buttonClassname}
                        onClick={handleButtonClick}
                    >
                        {children || (success ? <CheckIcon /> : <SaveIcon />)}
                    </Fab>
                    {loading && <CircularProgress size={68} className={classes.fabProgress} />}
                </div>
            ) : (
                <div className={classes.wrapper}>
                    <Button
                        variant="contained"
                        size={size}
                        color={color}
                        className={buttonClassname}
                        disabled={loading}
                        onClick={handleButtonClick}
                        startIcon={startIcon}
                        fullWidth
                    >
                        {children}
                    </Button>
                    {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                </div>
            )}
        </div>
    );
}
